/**
 * @fileoverview A validade do link: permanente ou temporária (1 hora, 24 horas,
 * 7 dias, 30 dias ou data e hora escolhidas, no fuso do navegador). Mostra até
 * quando vai valer; quem grava, em UTC, é o servidor.
 */

import React, { useMemo } from "react";
import TextField from "@material-ui/core/TextField";

import { i18n } from "../../translate/i18n";
import { formatDateTime } from "../../utils/formatLocaleDate";
import { PRESETS, customBounds, resolveExpiration } from "./shareHelpers";
import useStyles from "./styles";

const t = (key, options) => i18n.t(`boardShare.form.${key}`, options);

export const DEFAULT_EXPIRATION = { kind: "permanent", preset: "7d", custom: "" };

const ExpirationPicker = ({ value, onChange, disabled = false }) => {
  const classes = useStyles();
  const set = (patch) => onChange({ ...value, ...patch });
  const bounds = useMemo(() => customBounds(), []);
  const { expiresAt, invalid } = resolveExpiration(value);

  return (
    <div className={classes.section}>
      <p className={classes.label}>{t("validity")}</p>
      <div className={classes.segmented} role="radiogroup" aria-label={t("validity")}>
        {["permanent", "temporary"].map((kind) => (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={value.kind === kind}
            className={`${classes.segment} ${value.kind === kind ? classes.segmentActive : ""}`}
            onClick={() => set({ kind })}
            disabled={disabled}
          >
            {t(kind)}
          </button>
        ))}
      </div>
      {value.kind === "permanent" ? (
        <p className={classes.hint}>{t("permanentHelp")}</p>
      ) : (
        <>
          <div className={classes.chips} role="radiogroup" aria-label={t("temporary")}>
            {[...PRESETS.map((p) => p.key), "custom"].map((preset) => (
              <button
                key={preset}
                type="button"
                role="radio"
                aria-checked={value.preset === preset}
                className={`${classes.chip} ${value.preset === preset ? classes.chipActive : ""}`}
                onClick={() => set({ preset })}
                disabled={disabled}
              >
                {t(`presets.${preset}`)}
              </button>
            ))}
          </div>
          {value.preset === "custom" && (
            <TextField
              className={classes.field}
              type="datetime-local"
              label={t("customDate")}
              value={value.custom}
              onChange={(e) => set({ custom: e.target.value })}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: bounds.min, max: bounds.max }}
              disabled={disabled}
              error={Boolean(value.custom) && invalid}
            />
          )}
          {!invalid && <p className={classes.hint}>{t("expiresOn", { date: formatDateTime(expiresAt) })}</p>}
          {invalid && !value.custom && <p className={classes.hint}>{t("customRequired")}</p>}
          {invalid && value.custom && <p className={classes.error}>{t("customInvalid")}</p>}
        </>
      )}
    </div>
  );
};

export default ExpirationPicker;
